import { CellDrum, type CellDrumLanding } from "./CellDrum";
import type { HoldLandingProfile, HoldMotionTuning } from "./HoldMotionTuning";
import type { GridLayer, GridModel, PresentationTaskRunner, SpeedMode, SymbolLandEffect } from "@slot/presentation";

export interface HoldSpinMechanicDeps {
  /** Optional only for headless tests: without a runner `spinToModel` paints the model instantly. */
  taskRunner?: PresentationTaskRunner | null;
  motionTuning: HoldMotionTuning;
  /**
   * The ids a rolling cell's filler lanes are drawn from, asked for each cell as its roll starts (so a title's coin
   * values can follow its bet, and a cell's fillers its row: decision 56). Never empty: the mechanic invents no symbol.
   */
  fillerSymbolIds: (cellIndex: number) => readonly string[];
  speedModeProvider?: () => SpeedMode;
  /** The window each rolling cell shows its symbols through and its lane spacing (design units); default the cell. */
  window?: { width: number; height: number; laneSpacing: number } | null;
  /** The landing animation, played on each cell at its impact. */
  landEffect?: SymbolLandEffect | null;
  /** Each cell's impact (the bottom of its overshoot), in landing order; cells landing together come in reading order. */
  onCellLanded?: (index: number, symbolId: string) => void;
  /** An anticipated landing slot starts its extra roll: its cells, in reading order. */
  onAnticipationStart?: (indices: readonly number[]) => void;
}

/**
 * The hold-and-win mechanic over a `GridLayer` (decision 50): every cell is its own reel. A spin rolls each cell the
 * server does not hold — each in its own window (`CellDrum`) — and lands it on the server's board; a held cell keeps
 * its view and never moves. The player's spin starts the roll before the answer (`startRolling`); a frame inside a round
 * starts its own (`spinToModel`). When the result is known, the cells land in the title's order for the event
 * (`HoldLandingProfile`: column by column like reels, or one cell at a time), each after its least roll, an anticipated
 * slot rolling longer while the later ones wait.
 *
 * ── Scope: "hold_motion" ───────────────────────────────────────────────────────
 * Like the reels' drum, the cells run under their OWN runner scope, which the hold plugin declares no slam scope for:
 * the shell neither arms it in turbo nor force-finishes it. The mechanic reads the speed mode itself and runs its own
 * stop on the player's slam (`requestSlamStop`, through `GamePresentation.slam`): every cell still rolling brakes onto
 * its result together, held until the result is known — a cell only ever lands on the server's symbol.
 */
export class HoldSpinMechanic {
  public static readonly SCOPE = "hold_motion";

  private readonly grid: GridLayer;
  private readonly runner: PresentationTaskRunner | null;
  private readonly tuning: HoldMotionTuning;
  private readonly deps: HoldSpinMechanicDeps;
  private readonly settleEase: (t: number) => number;

  private readonly drums = new Map<number, CellDrum>();
  /** The result per rolling cell, once the frame's model is known. */
  private finals: Map<number, string> | null = null;
  /** Anticipated slots: when each one's tease starts, and its cells. */
  private teases: { at: number; indices: number[]; fired: boolean }[] = [];
  private run: Promise<void> | null = null;
  /** Bumped by every run and every abort: a superseded run's loop ends at its next frame and touches nothing. */
  private runId = 0;
  private now = 0;
  private slamRequested = false;
  private slamApplied = false;

  public constructor(grid: GridLayer, deps: HoldSpinMechanicDeps) {
    this.grid = grid;
    this.runner = deps.taskRunner ?? null;
    this.tuning = deps.motionTuning;
    this.deps = deps;
    this.settleEase = easing(deps.motionTuning.land.settleEasing);
  }

  /** True while any cell rolls. */
  public isRolling(): boolean {
    return this.drums.size > 0;
  }

  /**
   * Starts rolling `indices` now, before the result is known (the player's spin; a frame starts its own otherwise).
   * A cell already rolling keeps its roll. The views showing the cells become their drums' centre lanes.
   */
  public startRolling(indices: Iterable<number>): void {
    const runner = this.runner;
    if (!runner) return;
    const layout = this.grid.ensureLayout();
    const toStart = [...new Set(indices)].filter((index) => !this.drums.has(index));
    if (toStart.length === 0) return;
    // A new run's clock starts at 0 (the run starts below, after its first drums).
    if (!this.run) this.now = 0;
    if (this.drums.size === 0) {
      this.deps.landEffect?.finishAll();
      this.grid.notifySequenceStart();
    }

    // The grid forgets the cells that start rolling (their views move into the drums) and keeps every other.
    const cells = this.grid.cellSnapshot();
    this.grid.forgetAll();
    const starting = new Set(toStart);
    for (const [index, view] of cells) if (!starting.has(index)) this.grid.adopt(index, view);

    const gridRoot = this.grid.gridRoot;
    if (!gridRoot) throw new Error("HoldSpinMechanic: the grid has no root.");
    const window = this.deps.window ?? { width: layout.cellWidth, height: layout.cellHeight, laneSpacing: layout.cellHeight };
    const fullSpeed = this.tuning.roll.lanesPerSecond * window.laneSpacing;
    for (const index of toStart) {
      const centre = this.grid.getCellLocalPosition(index);
      const fillers = this.deps.fillerSymbolIds(index);
      if (fillers.length === 0) throw new Error(`HoldSpinMechanic: fillerSymbolIds(${index}) is empty.`);
      const pickFiller = (): string => fillers[Math.floor(Math.random() * fillers.length)]!;
      const drum = new CellDrum({
        index,
        host: gridRoot,
        pool: this.grid.pool,
        geometry: { x: centre.x, y: centre.y, windowWidth: window.width, windowHeight: window.height, laneSpacing: window.laneSpacing },
        current: cells.get(index) ?? null,
        pickFiller,
        fillerStretchY: this.tuning.roll.fillerStretchY,
      });
      drum.start(this.now, { fullSpeed, spinUpSeconds: this.tuning.roll.spinUpSeconds });
      this.drums.set(index, drum);
    }
    if (!this.run) this.run = this.drive(runner);
  }

  /**
   * Lands the frame `model`: every cell not in `held` rolls (if it was not rolling yet) and lands on its symbol in the
   * profile's order; a cell rolling since the player's spin lands on its symbol even when the server holds it. When
   * every cell has landed, the grid renders the model over the landed views, which re-binds a held cell whose symbol
   * the frame changed (no motion) and keeps every other. Without a runner the model is painted instantly.
   */
  public async spinToModel(
    model: GridModel,
    held: ReadonlySet<number>,
    profile: HoldLandingProfile,
    anticipatedColumns?: readonly number[],
    anticipatedCells?: readonly number[],
  ): Promise<void> {
    if (!this.runner) {
      this.renderModel(model);
      return;
    }
    const cellCount = this.grid.gridColumnCount * this.grid.gridRowCount;
    const toRoll: number[] = [];
    for (let index = 0; index < cellCount; index++) if (!held.has(index) && !this.drums.has(index)) toRoll.push(index);
    if (toRoll.length > 0) this.startRolling(toRoll);
    if (this.drums.size === 0) {
      this.renderModel(model);
      return;
    }
    const finals = new Map<number, string>();
    for (const index of this.drums.keys()) {
      const { row, col } = this.grid.layoutUtil.indexToRowCol(this.grid.gridColumnCount, index);
      finals.set(index, model.cells[row]?.[col]?.symbolId ?? "");
    }
    this.finals = finals;
    this.scheduleLandings(profile, new Set(anticipatedColumns ?? []), new Set(anticipatedCells ?? []));
    const myRun = this.runId;
    await this.run;
    // A recovery or a repaint superseded this spin while it rolled: the grid is theirs now.
    if (this.runId !== myRun) return;
    this.grid.renderGridModel(model, { preserveUnchanged: true });
  }

  /**
   * The player's slam: every cell still rolling brakes onto its result together — at once when the result is known,
   * else the moment it arrives. A cell already braking finishes its own stop. True when this call took the slam (once
   * per spin, and only while something rolls), so the shell plays the slam cue once.
   */
  public requestSlamStop(): boolean {
    if (this.drums.size === 0 || this.slamRequested) return false;
    this.slamRequested = true;
    return true;
  }

  /** Paints a model with no motion: any roll is dropped first; a cell already showing its symbol keeps its view. */
  public renderModel(model: GridModel): void {
    this.abortRolling();
    this.grid.renderGridModel(model, { preserveUnchanged: true });
  }

  /** Drops every roll without a stop (a rejected spin, a failure, a recovery): the rolling cells are left empty. */
  public abortRolling(): void {
    const release = this.release;
    for (const drum of this.drums.values()) drum.discard(release);
    this.drums.clear();
    this.endRun();
  }

  /** Grid recovery: every roll dropped, every landing back to rest, the grid cleared. */
  public resetPresentationState(): void {
    this.abortRolling();
    this.deps.landEffect?.finishAll();
    this.grid.resetPresentationState();
  }

  private readonly release = (view: Parameters<GridLayer["releaseView"]>[0]): void => this.grid.releaseView(view, "hold_cell_release");

  private endRun(): void {
    this.runId += 1;
    this.run = null;
    this.now = 0;
    this.finals = null;
    this.teases = [];
    this.slamRequested = false;
    this.slamApplied = false;
  }

  /**
   * The landing slots: when each rolling cell lands (its drum solves when to start braking), and when each anticipated
   * slot's tease starts. The first slot lands no earlier than a full brake after the least roll; each later one a
   * stagger after the previous, an anticipated slot (and every later one) the anticipation later still. A slot is
   * anticipated when one of its cells is, or the column of one (decision 54).
   */
  private scheduleLandings(profile: HoldLandingProfile, anticipatedColumns: ReadonlySet<number>, anticipatedCells: ReadonlySet<number>): void {
    const mode = this.deps.speedModeProvider?.() ?? "normal";
    const scale = mode === "normal" ? { staggerScale: 1, minRollScale: 1 } : this.tuning.speedModes[mode];
    const stagger = profile.staggerSeconds * scale.staggerScale;
    const minRoll = Math.max(profile.minRollSeconds * scale.minRollScale, this.tuning.roll.spinUpSeconds);
    let latestStart = 0;
    let longestBrake = 0;
    for (const [index, drum] of this.drums) {
      latestStart = Math.max(latestStart, drum.rollStartTime);
      longestBrake = Math.max(longestBrake, drum.brakeWindow(this.landingFor(index).overshootLanes).max);
    }
    const firstLand = Math.max(this.now, latestStart + minRoll) + longestBrake;

    const columns = this.grid.gridColumnCount;
    const indices = [...this.drums.keys()].sort((a, b) => a - b);
    const slots: number[][] = [];
    if (profile.order === "columns") {
      for (let col = 0; col < columns; col++) {
        const slot = indices.filter((index) => index % columns === col);
        if (slot.length > 0) slots.push(slot);
      }
    } else {
      for (const index of indices) slots.push([index]);
    }

    this.teases = [];
    let extra = 0;
    slots.forEach((slot, k) => {
      if (profile.anticipationSeconds > 0 && slot.some((index) => anticipatedCells.has(index) || anticipatedColumns.has(index % columns))) {
        // The tease starts as the slot before it lands (at once for the first).
        this.teases.push({ at: k === 0 ? this.now : firstLand + (k - 1) * stagger + extra, indices: slot, fired: false });
        extra += profile.anticipationSeconds;
      }
      const landAt = firstLand + k * stagger + extra;
      for (const index of slot) this.drums.get(index)!.planLanding(this.now, landAt, this.landingFor(index));
    });
  }

  /** A cell's stop: the tuning's overshoot and settle, or its result's own (decision 70). */
  private landingFor(index: number): CellDrumLanding {
    const land = this.tuning.land;
    const symbolId = this.finals?.get(index) ?? "";
    const own = land.bySymbol?.[symbolId];
    return { symbolId, overshootLanes: own?.overshootLanes ?? land.overshootLanes, settleSeconds: own?.settleSeconds ?? land.settleSeconds, settleEase: this.settleEase, unmaskLanes: land.unmaskLanes ?? 0 };
  }

  private async drive(runner: PresentationTaskRunner): Promise<void> {
    this.runId += 1;
    const myRun = this.runId;
    this.now = 0;
    try {
      await runner.runInScope(HoldSpinMechanic.SCOPE, () =>
        runner.tickInScope(
          HoldSpinMechanic.SCOPE,
          (now) => {
            if (this.runId === myRun) this.tick(now);
          },
          () => this.runId !== myRun || (this.finals !== null && this.drums.size === 0),
        ),
      );
    } finally {
      if (this.runId === myRun) {
        this.run = null;
        this.finals = null;
        this.teases = [];
        this.slamRequested = false;
        this.slamApplied = false;
        this.now = 0;
      }
    }
  }

  private tick(now: number): void {
    this.now = now;
    // The slam, once the result is known: every cell still rolling brakes onto it over the same short time.
    if (this.finals && this.slamRequested && !this.slamApplied) {
      this.slamApplied = true;
      for (const [index, drum] of this.drums) if (drum.isRolling) drum.slam(now, this.tuning.slamBrakeSeconds, this.landingFor(index));
    }

    const impacts: number[] = [];
    const landed: number[] = [];
    for (const [index, drum] of this.drums) {
      const step = drum.tick(now);
      if (step.impact) impacts.push(index);
      if (step.done) landed.push(index);
    }
    if (impacts.length > 0) this.dispatchImpacts(impacts.sort((a, b) => a - b));
    for (const index of landed) {
      const drum = this.drums.get(index)!;
      this.grid.adopt(index, drum.handOff(this.release));
      this.drums.delete(index);
    }
    // A tease starts after this frame's impacts: the slot before it has landed by then, and the title reads it.
    for (const tease of this.teases) {
      if (tease.fired || now < tease.at) continue;
      tease.fired = true;
      if (tease.indices.some((index) => this.drums.get(index)?.isRolling)) this.deps.onAnticipationStart?.(tease.indices);
    }
  }

  /** The landing reactions per column (the cells that hit together, top to bottom), the land effect, the title's callback. */
  private dispatchImpacts(indices: readonly number[]): void {
    const columns = this.grid.gridColumnCount;
    const finals = this.finals;
    const byColumn = new Map<number, number[]>();
    for (const index of indices) {
      const col = index % columns;
      const list = byColumn.get(col);
      if (list) list.push(index);
      else byColumn.set(col, [index]);
    }
    for (const col of [...byColumn.keys()].sort((a, b) => a - b)) {
      this.grid.notifyColumnLanded(col, { symbolIds: byColumn.get(col)!.map((index) => finals?.get(index) ?? "") });
    }
    for (const index of indices) {
      const symbolId = finals?.get(index) ?? "";
      const view = this.drums.get(index)?.finalView ?? null;
      if (view && symbolId) this.deps.landEffect?.play({ symbolId, cellNode: view.contentNode, symbolSprite: view.symbolSprite, index });
      this.deps.onCellLanded?.(index, symbolId);
    }
  }
}

/** The settle's easing: the few the tuning offers, as functions of progress 0..1. */
function easing(name: HoldMotionTuning["land"]["settleEasing"]): (t: number) => number {
  switch (name) {
    case "linear":
      return (t) => t;
    case "quadOut":
      return (t) => 1 - (1 - t) * (1 - t);
    case "cubicOut":
      return (t) => 1 - (1 - t) ** 3;
    case "sineInOut":
      return (t) => (1 - Math.cos(Math.PI * t)) / 2;
  }
}
